/**
 * Hours the chat spent on call together. The video's panel charts call hours
 * by month, which the recap doesn't have, so the panel shows the longest call
 * instead: its length counting up, its day and who was on it.
 */

import { Avatar, Fit, Scene, Zone, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import type { Chat } from "../../types";
import { count, shortDate } from "../shared";

const BLOBS: BlobSpec[] = [
  { colour: "yellow", x: 1010, y: 330, r: 150, seed: 18.3, wob: 0.22 },
  { colour: "fuchsia", x: 60, y: 1820, r: 220, seed: 19.1, wob: 0.18 },
];

const PANEL_TOP = 900;
const PEOPLE_TOP = 320;
const ROW = 76;
/** Up to four people sit one to a row, like the video's lanes; more go two to a row, eight cells at most. */
const CELLS = 8;

const dayOf = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
};
/** "Sat 21 Feb". */
const dayLabel = (iso: string) => `${dayOf(iso).toLocaleDateString("en-GB", { weekday: "short" })} ${shortDate(iso)}`;

/** "6 h 11 min", or "42 min" for a short one. */
const length = (minutes: number) => {
  const m = Math.round(minutes);
  return m >= 60 ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} min` : `${m} min`;
};

export function ChatCalls({ calls }: { calls: NonNullable<Chat["calls"]> }) {
  const { longest } = calls;
  // A quiet chat's few hours keep a decimal rather than rounding to nothing.
  const hours = calls.hours >= 10 ? Math.round(calls.hours) : Math.round(calls.hours * 10) / 10;
  const hoursText = (v: number) => (Number.isInteger(hours) ? Math.round(v).toLocaleString("en-US") : v.toFixed(1));
  const callsText = `${count(calls.count)} ${calls.count === 1 ? "call" : "calls"}`;
  const days = calls.hours >= 48 ? Math.floor(calls.hours / 24) : 0;

  const people = longest.people;
  const shown = people.length > CELLS ? people.slice(0, CELLS - 1) : people;
  const more = people.length - shown.length;
  const cols = people.length > 4 ? 2 : 1;
  const rows = Math.ceil((shown.length + (more ? 1 : 0)) / cols);
  const panel = people.length ? PEOPLE_TOP + rows * ROW - 12 + 48 : 330;

  const root = useTimeline((s) => {
    s.rise(".kick", 0.35, { y: 0 });
    s.rise(".c-row", 0.4, { y: 40, sound: false });
    s.count(s.q(".c-big")[0], hours, 0.45, 1.3, hoursText, { ease: "power3.out" });
    s.rise("[data-part=sub]", 1.1, { y: 24 });
    s.tl.fromTo(".cpanel", { autoAlpha: 0, y: 160 }, { autoAlpha: 1, y: 0, duration: 0.6 }, 1.25);
    s.sound("whoosh", 1.25);
    s.count(s.q(".dur")[0], longest.minutes, 1.55, 1.4, length);
    if (people.length) {
      s.pop(s.q(".who .av"), 1.75, { stagger: 0.1 });
      s.rise(s.q(".who-name"), 1.8, { y: 0, x: 30, stagger: 0.1, sound: false });
    }
    if (days) s.rise("[data-part=foot]", 2.6, { y: 24 });
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div className="kick">Hours on call together</div>
        </Zone>
        <Zone at="middle">
          {/* The number and its unit shrink together if a big year runs wide. */}
          <Fit size={380} min={200} className="c-row" style={{ right: 60 }}>
            <div className="c-big disp" style={{ fontSize: "1em" }}>
              {hoursText(hours)}
            </div>
            <div className="c-unit disp" style={{ fontSize: "0.316em", paddingBottom: "0.047em" }}>
              {hours === 1 ? "hour" : "hours"}
            </div>
          </Fit>
          <div data-part="sub" className="line" style={{ top: 730 }}>
            {days ? `That's ${days} whole days of talking.` : `on call together, across ${callsText}.`}
          </div>
        </Zone>
        <Zone at="bottom">
          <div className="cpanel" style={{ height: panel }}>
            <div className="k2">THE LONGEST CALL</div>
            <div className="when">{dayLabel(longest.date)}</div>
            <div className="dur disp">{length(longest.minutes)}</div>
            {people.length ? (
              <div
                className="abs"
                style={{
                  left: 48,
                  right: 48,
                  top: PEOPLE_TOP,
                  display: "grid",
                  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                  columnGap: 30,
                  rowGap: ROW - 64,
                }}
              >
                {shown.map((name, i) => (
                  <div key={i} className="who" style={{ display: "flex", alignItems: "center", gap: 22, height: 64 }}>
                    <Avatar name={name} size={64} inline style={{ borderWidth: 0 }} />
                    <Fit size={44} min={22} className="disp who-name" style={{ flex: 1, minWidth: 0 }}>
                      {name}
                    </Fit>
                  </div>
                ))}
                {more ? (
                  <div className="who-name mono" style={{ alignSelf: "center", fontSize: 28, fontWeight: 700, color: "var(--wr-muted)" }}>
                    +{more} MORE
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          {days ? (
            <div data-part="foot" className="line" style={{ top: PANEL_TOP + panel + 40 }}>
              Across {callsText}.
            </div>
          ) : null}
        </Zone>
      </Scene>
    </div>
  );
}
